import * as ents from '@attain-apps/entities'

import type * as mosaicModels from '../models'

import * as models from './models'

export interface State {
  charts: models.Chart.Lookup
  isUploadingResumeFile: boolean
}
export namespace State {
  export const INITIAL: State = {
    charts: {},
    isUploadingResumeFile: false,
  }

  /**
   * Updates a specific {@link models.Chart} in {@link State}
   */
  export function patchChart({
    chartId,
    state,
    apply,
  }: models.Chart.Identifier & {
    readonly state: State
    readonly apply:
      | models.Chart
      | ((chart: models.Chart | undefined) => models.Chart | undefined | void)
  }): State {
    const oldChart: models.Chart | undefined = state.charts[chartId]
      ? { ...state.charts[chartId] }
      : undefined

    const newChart =
      typeof apply === 'function' ? (apply(oldChart) ?? oldChart) : apply
    if (!newChart) {
      return state
    }
    return {
      ...state,
      charts: {
        ...state.charts,
        [chartId]: newChart,
      },
    }
  }

  /**
   * Initializes the current chart entry for a tree-editor widget from the
   * widget-rendered tree payload.
   */
  export function init({
    state,
    tree: { defaultModelId, displayBindings, edgesFrom, nodes },
    widgetRefId,
  }: {
    readonly tree: ents.core.display.tree.Tree
    readonly widgetRefId: mosaicModels.widget.Ref.Id
    readonly state: State
  }): State {
    state = {
      ...state,
    }

    return patchChart({
      apply: {
        areEdgesLoaded: false,
        edges: [],
        edits: {},
        history: [],
        metadata: {
          defaultModelId,
          displayBindings,
          edgesFrom,
        },
        nodes,
        peopleInsightsLookup: {},
        undoCount: 0,
      },
      chartId: models.Chart.Id.from(widgetRefId),
      state,
    })
  }

  /**
   * Ensures a node has an editable draft in the chart and optionally patches it.
   */
  export function patchNodeEdit({
    apply,
    chartId,
    nodeId,
    state,
  }: models.Chart.Identifier & {
    readonly nodeId: ents.core.display.tree.Node.Id
    readonly state: State
    readonly apply?: (
      node: ents.core.display.tree.Node,
    ) => ents.core.display.tree.Node
  }): State {
    return patchChart({
      apply: chart => {
        if (!chart) {
          return
        }

        const editedNode =
          chart.edits[nodeId] ??
          chart.nodes.find(
            node => ents.core.display.tree.Node.Id.get(node) === nodeId,
          )
        if (!editedNode) {
          return
        }

        return {
          ...chart,
          currentlyEditingNodeId: nodeId,
          edits: {
            ...chart.edits,
            [nodeId]: apply ? apply(editedNode) : editedNode,
          },
        }
      },
      chartId,
      state,
    })
  }

  /**
   * Updates a specific {@link models.Chart} in {@link State}
   */
  export function applyChangeToChart({
    chartId,
    state,
    change: changeArg,
  }: models.Chart.Identifier & {
    readonly state: State
    readonly change:
      | models.Change
      | ((chart: models.Chart | undefined) => models.Change | undefined)
  }): State {
    return patchChart({
      apply: oldChart => {
        const change =
          typeof changeArg === 'function' ? changeArg(oldChart) : changeArg
        if (!change) {
          return undefined
        }
        return {
          ...models.Change.appliedTo(
            // We used data from the existing org chart, if there is one.
            // Otherwise, we create empty data
            oldChart ?? {
              edges: [],
              nodes: [],
            },
            change,
          ),
          areEdgesLoaded: oldChart?.areEdgesLoaded ?? false,
          currentlyEditingNodeId: oldChart?.currentlyEditingNodeId,
          edits: oldChart?.edits ?? {},
          history: [...(oldChart?.history ?? []), change],
          metadata: oldChart?.metadata ?? {},
          peopleInsightsLookup: oldChart?.peopleInsightsLookup ?? {},
          // We always reset to 0 on any new change
          undoCount: 0,
        }
      },
      chartId,
      state,
    })
  }

  /**
   * Updates a specific {@link models.Chart} in {@link State}
   */
  export function moveThroughHistoryInOrgChart({
    chartId: orgChartId,
    state,
    steps,
  }: models.Chart.Identifier & {
    readonly state: State
    /**
     * 1 = undo
     *
     * -1 = redo
     */
    readonly steps: 1 | -1
  }): State {
    return State.patchChart({
      apply: orgChart => {
        if (!orgChart) {
          return undefined
        }
        const oldUndoCount = orgChart.undoCount
        const newUndoCount = oldUndoCount + steps
        if (newUndoCount < 0 || newUndoCount > orgChart.history.length) {
          return undefined
        }
        // positive = undo, negative = redo
        const changeToApply =
          steps > 0
            ? models.Change.inverse(
                orgChart.history[orgChart.history.length - newUndoCount],
              )
            : orgChart.history[orgChart.history.length - oldUndoCount]

        return {
          ...orgChart,
          ...models.Change.appliedTo(orgChart, changeToApply),
          undoCount: newUndoCount,
        }
      },
      chartId: orgChartId,
      state,
    })
  }
}
